import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CompanyOwnerUsersSection } from "@/components/companies/CompanyOwnerUsersSection";
import { CompanyLogoUploadSection } from "@/components/companies/CompanyLogoUploadSection";
import { CompanyPrimaryColorSection } from "@/components/companies/CompanyPrimaryColorSection";
import { applyPrimaryColorToDom } from "@/lib/theme";
import { TimezoneSelect } from "@/components/companies/TimezoneSelect";
import { CurrencySelect } from "@/components/companies/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import {
  companyFormErrorMessage,
  companyFormToPayload,
  emptyCompanyForm,
  recordToCompanyForm,
  type CompanyFormState,
} from "@/lib/admin/companyForm";
import {
  fetchCompanyById,
  fetchCompanyOwnerRolePermissions,
  updateCompany,
  updateCompanyOwnerRolePermissions,
  type CompanyAssignedUser,
} from "@/lib/admin/companiesStore";
import { listManagedPermissions, syncManagedPermissionsFromApi } from "@/lib/admin/managedPermissionsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { Accessibility, ArrowLeft, Building2, ListTree, Loader2, Save, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";

type GroupedPerm = { category: string; items: Array<{ name: string; title: string }> };

/** Country dropdown is Pakistan-only for now (matches Add Company pattern). */
const PAKISTAN_ONLY = "Pakistan" as const;
const COUNTRY_SELECT_NONE = "__country_none__";
const PROVINCE_SELECT_NONE = "__province_none__";
const PAKISTAN_PROVINCES = [
  "Punjab",
  "Sindh",
  "Khyber Pakhtunkhwa",
  "Balochistan",
  "Islamabad Capital Territory",
  "Gilgit-Baltistan",
  "Azad Jammu and Kashmir",
] as const;

function PakistanCountrySelect({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const trimmed = value.trim();
  const orphan = trimmed && trimmed !== PAKISTAN_ONLY ? trimmed : null;
  const selectValue = orphan ?? (!trimmed ? COUNTRY_SELECT_NONE : PAKISTAN_ONLY);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Country</Label>
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === COUNTRY_SELECT_NONE) onChange("");
          else onChange(v);
        }}
      >
        <SelectTrigger className="glass-input h-10">
          <SelectValue placeholder="Country" />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem value={COUNTRY_SELECT_NONE}>Select…</SelectItem>
          {orphan ? (
            <SelectItem value={orphan}>
              {orphan} <span className="text-muted-foreground">(not in list)</span>
            </SelectItem>
          ) : null}
          <SelectItem value={PAKISTAN_ONLY}>{PAKISTAN_ONLY}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function PakistanProvinceSelect({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const trimmed = value.trim();
  const provinces = PAKISTAN_PROVINCES as readonly string[];
  const inList = Boolean(trimmed && provinces.includes(trimmed));
  const orphan = trimmed && !inList ? trimmed : null;
  const selectValue = orphan ?? (inList ? trimmed : PROVINCE_SELECT_NONE);

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">State</Label>
      <Select value={selectValue} onValueChange={(v) => onChange(v === PROVINCE_SELECT_NONE ? "" : v)}>
        <SelectTrigger className="glass-input h-10">
          <SelectValue placeholder="Select state" />
        </SelectTrigger>
        <SelectContent position="popper">
          <SelectItem value={PROVINCE_SELECT_NONE}>Select…</SelectItem>
          {orphan ? (
            <SelectItem value={orphan}>
              {orphan} <span className="text-muted-foreground">(not in list)</span>
            </SelectItem>
          ) : null}
          {PAKISTAN_PROVINCES.map((p) => (
            <SelectItem key={p} value={p}>
              {p}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function checkedState(selectedCount: number, totalCount: number): boolean | "indeterminate" {
  if (totalCount <= 0) return false;
  if (selectedCount <= 0) return false;
  if (selectedCount >= totalCount) return true;
  return "indeterminate";
}

function groupPermissionNamesByCategory(names: readonly string[]): GroupedPerm[] {
  if (names.length === 0) return [];
  const byName = new Map(listManagedPermissions().map((p) => [p.name, p]));
  type Row = { name: string; title: string; category: string };
  const rows: Row[] = names.map((name) => {
    const rec = byName.get(name);
    return {
      name,
      title: rec?.title?.trim() ? rec.title : name,
      category: rec?.categoryName?.trim() || "Uncategorized",
    };
  });
  const byCat = new Map<string, Row[]>();
  for (const row of rows) {
    if (!byCat.has(row.category)) byCat.set(row.category, []);
    byCat.get(row.category)!.push(row);
  }
  return Array.from(byCat.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, items]) => ({
      category,
      items: [...items].sort((a, b) => a.title.localeCompare(b.title)),
    }));
}

const SectionHeader = ({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle?: string;
  icon: ComponentType<{ className?: string }>;
}) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
      {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
    </div>
  </div>
);

const EditCompany = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canUpdate = can(user, "company_can_update");

  const [form, setForm] = useState<CompanyFormState>(emptyCompanyForm());
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState<CompanyAssignedUser[]>([]);
  const loadedSnapshot = useRef<{ form: CompanyFormState; assignedUsers: CompanyAssignedUser[] } | null>(null);

  const [accessPreviewLoading, setAccessPreviewLoading] = useState(false);
  const [ownerRoleName, setOwnerRoleName] = useState("Company Owner");
  /** Company-scoped Company Owner role: checked permission names (may include extras beyond the Default Company template). */
  const [ownerPermDraft, setOwnerPermDraft] = useState<Set<string>>(() => new Set());
  const [rightPermFilter, setRightPermFilter] = useState("");
  const [permCatalogVersion, setPermCatalogVersion] = useState(0);

  const handleCancel = useCallback(() => navigate("/companies"), [navigate]);

  const resetAll = () => {
    const s = loadedSnapshot.current;
    if (!s) return;
    setForm({ ...s.form });
    setAssignedUsers(s.assignedUsers.map((u) => ({ ...u })));
    setLogoFile(null);
  };

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let mounted = true;
    void (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const record = await fetchCompanyById(id);
        if (!mounted) return;
        const nextForm = recordToCompanyForm(record);
        setForm(nextForm);
        setAssignedUsers(record.companyAssignedUsers);
        loadedSnapshot.current = { form: nextForm, assignedUsers: record.companyAssignedUsers };
      } catch (e) {
        console.error("[EditCompany] Load failed", e);
        if (mounted) {
          if (isAxiosError(e) && e.response?.status === 404) setNotFound(true);
          else toast.error(companyFormErrorMessage(e));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id || loading || notFound) return;
    let cancelled = false;
    setAccessPreviewLoading(true);
    setOwnerPermDraft(new Set());
    void (async () => {
      try {
        // Edit-company must ONLY use the company-scoped role for this tenant.
        // No template (company_id NULL) role is loaded or referenced here.
        await syncManagedPermissionsFromApi();
        if (!cancelled) setPermCatalogVersion((v) => v + 1);
        const preview = await fetchCompanyOwnerRolePermissions(id);
        if (!cancelled) {
          setOwnerRoleName("Company Owner");
          setOwnerPermDraft(new Set(preview.permissionNames));
        }
      } catch (e) {
        console.error("[EditCompany] Access preview load failed", e);
        if (!cancelled) {
          setOwnerPermDraft(new Set());
        }
        if (!cancelled) toast.error(companyFormErrorMessage(e));
      } finally {
        if (!cancelled) setAccessPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loading, notFound]);

  const allPermissionNames = useMemo(() => {
    return listManagedPermissions()
      .map((p) => p.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [permCatalogVersion]);

  const allPermissionCount = allPermissionNames.length;
  const selectedCount = ownerPermDraft.size;
  const selectAllState = checkedState(selectedCount, allPermissionCount);

  const selectAll = () => setOwnerPermDraft(new Set(allPermissionNames));
  const clearAll = () => setOwnerPermDraft(new Set());

  const filteredRightGrouped = useMemo(() => {
    const q = rightPermFilter.trim().toLowerCase();
    const names = q ? allPermissionNames.filter((n) => n.toLowerCase().includes(q)) : allPermissionNames;
    return groupPermissionNamesByCategory(names);
  }, [allPermissionNames, rightPermFilter]);

  const save = async () => {
    if (isSaving || !canUpdate || !id) return;
    if (!form.name.trim()) {
      toast.error("Company name is required.");
      return;
    }
    setIsSaving(true);
    try {
      const r = await updateCompany(id, companyFormToPayload(form), { logoFile });
      if (!accessPreviewLoading && allPermissionNames.length > 0) {
        const permRes = await updateCompanyOwnerRolePermissions(id, [...ownerPermDraft].sort((a, b) => a.localeCompare(b)));
        setOwnerPermDraft(new Set(permRes.permissionNames));
      }
      const record = await fetchCompanyById(id);
      setAssignedUsers(record.companyAssignedUsers);
      const nextForm = recordToCompanyForm(record);
      setForm(nextForm);
      loadedSnapshot.current = { form: nextForm, assignedUsers: record.companyAssignedUsers };
      if (user?.company_id === Number(id)) {
        applyPrimaryColorToDom(form.primaryColor);
      }
      toast.success(r.message);
      navigate(`/companies/${id}`);
    } catch (e) {
      console.error("[EditCompany] Save failed", e);
      toast.error(companyFormErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canUpdate) {
    return (
      <PermissionDenied
        title="Cannot edit companies"
        message="You don’t have permission to update companies. Ask an administrator to grant company_can_update."
      />
    );
  }

  if (!id) {
    return (
      <Layout title="Edit company">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing company id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Edit company">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          Loading company…
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout title="Edit company">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Company not found.</div>
          <Button variant="glass" className="gap-2" asChild>
            <Link to="/companies">
              <ArrowLeft className="w-4 h-4" />
              Back to companies
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Edit company">
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground">Edit company</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Logo, company profile, and company-owner accounts—same layout as{" "}
                <Link to="/companies/add" className="text-primary font-medium hover:underline">
                  Add company
                </Link>
                .
              </p>
            </div>
          </div>
          <Button type="button" variant="glass" onClick={handleCancel}>
            Cancel
          </Button>
        </div>

        <CompanyLogoUploadSection
          logoFile={logoFile}
          onLogoFileChange={setLogoFile}
          existingImageUrl={logoFile ? null : form.logo || null}
          onRemoveExistingImage={() => setForm((f) => ({ ...f, logo: "" }))}
        />

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-semibold text-foreground text-sm">Company profile</h3>
                <p className="text-xs text-muted-foreground">Name, domain, and contact details.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 sm:ml-auto">
              <span
                className={form.status === "active" ? "text-xs text-muted-foreground" : "text-xs font-medium text-foreground"}
              >
                Inactive
              </span>
              <Switch
                id="edit-company-status"
                checked={form.status === "active"}
                onCheckedChange={(on) => setForm((f) => ({ ...f, status: on ? "active" : "inactive" }))}
                aria-label="Company active"
              />
              <span
                className={form.status === "active" ? "text-xs font-medium text-foreground" : "text-xs text-muted-foreground"}
              >
                Active
              </span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-4">
                <p className="text-xs font-medium italic text-muted-foreground">Company Identity</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Company Name *</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Company name"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Domain</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.domain}
                      onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                      placeholder="example.com"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      type="email"
                      autoComplete="off"
                      placeholder="company@email.com"
                    />
                  </div>
                </div>
              </div>

              <CompanyPrimaryColorSection
                value={form.primaryColor}
                onChange={(c) => setForm((f) => ({ ...f, primaryColor: c }))}
              />

              <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-4">
                <p className="text-xs font-medium italic text-muted-foreground">Location Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <PakistanCountrySelect value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v }))} />
                  <PakistanProvinceSelect value={form.region} onChange={(v) => setForm((f) => ({ ...f, region: v }))} />
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">City</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.city}
                      onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                      placeholder="City"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Address</Label>
                  <Textarea
                    className="glass-input min-h-[80px] resize-y"
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="Street / area / details"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TimezoneSelect value={form.timezone} onChange={(v) => setForm((f) => ({ ...f, timezone: v }))} />
                  <CurrencySelect value={form.currencyId} onChange={(v) => setForm((f) => ({ ...f, currencyId: v }))} />
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-4">
                <p className="text-xs font-medium italic text-muted-foreground">Contact Person (who manages the account)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Contact Person (Full Name)</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.contactPerson}
                      onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Contact Number</Label>
                    <Input
                      className="glass-input h-10"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="Phone"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.03 }}
          className="glass-card p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <SectionHeader
              title="Users assigned to this company"
              subtitle="Every user with users.company_id set to this tenant (any account role). Contact person defaults to a company-owner account when present."
              icon={Users}
            />
          </div>

          <CompanyOwnerUsersSection users={assignedUsers} embedded />
          <p className="text-xs text-muted-foreground mt-3">
            Need another owner account? Create a company with an initial user on{" "}
            <Link to="/companies/add" className="text-primary font-medium hover:underline">
              Add company
            </Link>{" "}
            or link users via your employee / access workflow.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="glass-card p-6"
        >
          <SectionHeader
            title="Accessibility"
            subtitle="Company Owner role for this tenant: checked permissions are assigned; unchecked are not."
            icon={Accessibility}
          />

          {accessPreviewLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-10 justify-center border border-border/40 rounded-lg bg-background/50">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
              Loading accessibility…
            </div>
          ) : (
            <div className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-4 min-h-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-border/40 bg-card/60 dark:bg-card/30 px-3 py-2.5 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-semibold text-foreground min-w-0">
                  <ListTree className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" aria-hidden />
                  <span className="truncate">{ownerRoleName}</span>
                  <span className="text-muted-foreground font-normal shrink-0">· this company</span>
                </div>
                <Input
                  className="glass-input h-9 text-xs sm:max-w-[220px]"
                  placeholder="Search permissions…"
                  value={rightPermFilter}
                  onChange={(e) => setRightPermFilter(e.target.value)}
                />
              </div>
              <ScrollArea className="h-[min(52vh,480px)] pr-3">
                {allPermissionNames.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">No permissions in the catalog yet.</p>
                ) : filteredRightGrouped.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">No permissions match this search.</p>
                ) : (
                  <div className="space-y-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/50 bg-card/70 dark:bg-card/25 px-3 py-2.5 shadow-sm">
                      <label className={cn("flex items-center gap-2 text-xs font-medium", canUpdate && !isSaving ? "cursor-pointer" : "cursor-not-allowed")}>
                        <Checkbox
                          checked={selectAllState}
                          disabled={!canUpdate || isSaving}
                          onCheckedChange={(v) => {
                            if (!canUpdate || isSaving) return;
                            if (v === true) selectAll();
                            else clearAll();
                          }}
                        />
                        <span className="text-muted-foreground">Select all</span>
                      </label>
                      <div className="text-[11px] text-muted-foreground tabular-nums">
                        <span className="text-foreground font-medium">{selectedCount}</span> assigned ·{" "}
                        <span>{allPermissionCount - selectedCount}</span> unassigned
                      </div>
                    </div>
                    {filteredRightGrouped.map((g) => (
                      <div key={g.category} className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-0.5">
                          {g.category}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                          {g.items.map((p) => {
                            const checked = ownerPermDraft.has(p.name);
                            return (
                              <label
                                key={p.name}
                                className={cn(
                                  "flex items-start gap-2.5 rounded-xl border p-3 shadow-sm transition-colors",
                                  checked
                                    ? "border-primary/40 bg-primary/10 dark:bg-primary/15 ring-1 ring-primary/20"
                                    : "border-border/60 bg-card/90 dark:bg-card/40 hover:border-border hover:bg-muted/30",
                                  canUpdate && !isSaving ? "cursor-pointer" : "cursor-not-allowed opacity-75",
                                )}
                              >
                                <Checkbox
                                  checked={checked}
                                  disabled={!canUpdate || isSaving}
                                  onCheckedChange={(v) => {
                                    if (!canUpdate || isSaving) return;
                                    setOwnerPermDraft((prev) => {
                                      const next = new Set(prev);
                                      if (v === true) next.add(p.name);
                                      else next.delete(p.name);
                                      return next;
                                    });
                                  }}
                                  className="mt-0.5 shrink-0"
                                  aria-label={checked ? `${p.title} assigned` : `${p.title} not assigned`}
                                />
                                <span className="min-w-0 flex-1">
                                  <span className="block text-xs font-medium text-foreground leading-snug">{p.title}</span>
                                  <span className="block text-[10px] text-muted-foreground font-mono break-all mt-0.5">{p.name}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <p className="text-[11px] text-muted-foreground pt-1 border-t border-border/30">
                Checked = assigned to this company’s Company Owner role. Use <span className="font-medium text-foreground">Update company</span>{" "}
                below to save the profile and accessibility together.
              </p>
            </div>
          )}
        </motion.div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-border/40">
          <Button type="button" variant="glass" onClick={resetAll} disabled={isSaving}>
            Reset form
          </Button>
          <Button type="button" variant="premium" onClick={() => void save()} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating…
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Update company
              </>
            )}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default EditCompany;
